import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useScrollToTop } from '../../util/scrollToTop';
import { useBookingStudio } from '../../hooks/useBookingStudio';
import { useBooking } from '../../store/bookingStore';
import { NavBarContext } from '../../App';
import { getSelectedPackages } from '../../util/getSelectedPackages';
import TotalBox from './TotalBox';
import {
  buildBookingAuthNavigationState,
  buildStudioBookingRedirectLocation,
  isBookingAuthError,
  storeStudioBookingRedirect,
} from '../../util/bookingAuthRedirect';
import {
  buildBookingCheckoutPayload,
  buildBookingPayloadSignature,
  getBookingDurationInHours,
} from '../../util/bookingPayload';
import { calculateCreativePackagesPrice } from '../../util/calculateCreativePackagesPrice';
import { getReservationStripeRoute } from '../../util/stripeReservationRoute';
import { getReservationAmountGate } from '../../util/bookingServerAmountSync';
import { logBookingDiagnostic } from '../../util/bookingDiagnostics';
import { trackBookingPurchased } from '../../util/tracking';
import PreparedStripePaymentSection from './PreparedStripePaymentSection';
import { usePreparedBookingPaymentFlow } from './usePreparedBookingPaymentFlow';
import classes from './Checkout.module.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const DIAG = '[Booking Checkout]';
const PAYMENT_DETAILS_ERROR_MESSAGE = 'Zahlungsdaten konnten nicht geprüft werden. Bitte prüfe deine Angaben.';
const PAYMENT_CONFIRMATION_ERROR_MESSAGE = 'Zahlung konnte nicht bestätigt werden. Bitte prüfe deine Zahlungsdaten oder versuche es erneut.';
const BOOKING_PREPARATION_ERROR_MESSAGE = 'Die Buchung konnte gerade nicht vorbereitet werden. Bitte versuche es erneut.';
const BOOKING_CREATION_AFTER_PAYMENT_ERROR_MESSAGE = 'Deine Zahlung wurde autorisiert, aber die Buchung konnte nicht erstellt werden. Bitte kontaktiere den Support.';
const BOOKING_AUTH_RECOVERY_MESSAGE = 'Deine Zahlung wurde autorisiert. Bitte melde dich erneut an, damit wir die Buchung abschließen können.';
const SERVER_AMOUNT_REFRESH_MESSAGE = 'Der finale Buchungsbetrag wurde aktualisiert. Bitte prüfe den Betrag und bestätige die Zahlung erneut.';

const PAYMENT_FLOW_MESSAGES = {
  bookingPreparationError: BOOKING_PREPARATION_ERROR_MESSAGE,
  paymentDetailsError: PAYMENT_DETAILS_ERROR_MESSAGE,
  paymentConfirmationError: PAYMENT_CONFIRMATION_ERROR_MESSAGE,
};

const createUserFacingError = (message) => {
  const error = new Error(message);
  error.userFacing = true;
  return error;
};

const Checkout = ({ onPrevSlide, showConfirmation, studio, studioCreativesData }) => {
  const stepScrollRootRef = useRef(null);
  useScrollToTop({ delay: 50, rootRef: stepScrollRootRef });
  const navigate = useNavigate();
  const location = useLocation();
  const { isPhoneSize } = React.useContext(NavBarContext);

  const { bookingData, updateBookingData } = useBooking();
  const { handleSlotReservation, handleBookingConfirmation, loading, error } = useBookingStudio();
  const [bookingError, setBookingError] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const reservationCacheRef = useRef(null);

  const projectNameRef = useRef(null);
  const projectDescriptionRef = useRef(null);
  const paymentMethodRef = useRef(null);

  const isPackagePricing = useMemo(() => studio?.pricingMode === 'PACKAGES', [studio?.pricingMode]);

  const stripeUser = studio?.User;
  const initialStripeRoute = useMemo(() => {
    const accountId = stripeUser?.stripeConnectAccountId || null;
    return {
      canUseConnect: Boolean(
        accountId
          && stripeUser?.stripeChargesEnabled === true
          && stripeUser?.stripePayoutsEnabled === true
          && stripeUser?.stripeOnboardingComplete === true
      ),
      stripeConnectAccountId: accountId,
    };
  }, [
    stripeUser?.stripeConnectAccountId,
    stripeUser?.stripeChargesEnabled,
    stripeUser?.stripePayoutsEnabled,
    stripeUser?.stripeOnboardingComplete,
  ]);
  const [serverStripeRoute, setServerStripeRoute] = useState(null);

  useEffect(() => {
    setServerStripeRoute(null);
    reservationCacheRef.current = null;
  }, [studio?.id]);

  const activeStripeRoute = serverStripeRoute || initialStripeRoute;
  const canUseConnect = activeStripeRoute.canUseConnect;
  const stripeConnectAccountId = activeStripeRoute.stripeConnectAccountId;

  const handleReservationStripeRoute = useCallback((reservation) => {
    const route = getReservationStripeRoute(reservation);
    if (!route) return;

    setServerStripeRoute((previousRoute) => {
      if (
        previousRoute?.canUseConnect === route.canUseConnect
        && previousRoute?.stripeConnectAccountId === route.stripeConnectAccountId
      ) {
        return previousRoute;
      }
      return route;
    });
  }, []);

  const totalAmountCents = useMemo(() => {
    const euros = Number(bookingData?.totalPrice);
    if (!Number.isFinite(euros) || euros <= 0) return 0;
    return Math.round(euros * 100);
  }, [bookingData?.totalPrice]);

  const pricingUnavailableMessage =
    bookingData?.pricingUnavailableReason || 'Preis für diese Personengruppe nicht verfügbar.';
  const canPreparePayment =
    !bookingData?.pricingUnavailable
    && totalAmountCents > 0
    && bookingData?.date
    && bookingData?.startTime
    && bookingData?.endTime;

  const ensureReservationAmountReady = useCallback((reservation) => {
    const gate = getReservationAmountGate({
      reservation,
      currentAmountCents: totalAmountCents,
    });

    if (!gate.needsUserReconfirmation) {
      return true;
    }

    gate.updateEntries.forEach(([field, value]) => {
      updateBookingData(field, value);
    });
    setBookingError(SERVER_AMOUNT_REFRESH_MESSAGE);
    return false;
  }, [totalAmountCents, updateBookingData]);

  const clearFieldError = (fieldName) => {
    if (!fieldErrors[fieldName]) return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const scrollToFirstError = (errors) => {
    const errorOrder = ['projectName', 'projectDescription', 'paymentMethod'];
    for (const field of errorOrder) {
      if (!errors[field]) continue;
      const ref = field === 'projectName' ? projectNameRef : field === 'projectDescription' ? projectDescriptionRef : paymentMethodRef;
      if (ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      break;
    }
  };

  const validateProjectFields = useCallback(() => {
    const errors = {};
    if (!bookingData.projectName || bookingData.projectName.trim() === '') {
      errors.projectName = 'Projektname eingeben';
    } else if (bookingData.projectName.trim().length < 3) {
      errors.projectName = 'Projektname muss mindestens 3 Zeichen lang sein';
    }
    if (!bookingData.description || bookingData.description.trim() === '') {
      errors.projectDescription = 'Projektbeschreibung eingeben';
    } else if (bookingData.description.trim().length < 10) {
      errors.projectDescription = 'Projektbeschreibung muss mindestens 10 Zeichen lang sein';
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      scrollToFirstError(errors);
      return false;
    }
    return true;
  }, [bookingData.projectName, bookingData.description]);

  const buildBookingPayload = useCallback(() => {
    return buildBookingCheckoutPayload({
      bookingData,
      studioId: studio?.id,
      isPackagePricing,
    });
  }, [bookingData, studio?.id, isPackagePricing]);

  const buildPayloadSignature = useCallback(buildBookingPayloadSignature, []);

  const runSlotReservation = useCallback(
    (payload) => handleSlotReservation(payload, studioCreativesData),
    [handleSlotReservation, studioCreativesData]
  );

  const handleAuthRequired = useCallback((payload) => {
    const target = buildStudioBookingRedirectLocation({
      studioId: payload?.studioID || studio?.id,
      isPhoneSize,
      currentLocation: location,
    });

    storeStudioBookingRedirect({
      studioId: payload?.studioID || studio?.id,
      isPhoneSize,
      currentLocation: location,
    });

    const state = buildBookingAuthNavigationState({ location, from: target });
    navigate(isPhoneSize ? '/anmelden' : '/', { state });
  }, [isPhoneSize, location, navigate, studio?.id]);

  const finalizeBookingAfterAuthorizedPayment = useCallback(
    async (reservation, payload) => {
      let bookingResult = null;
      const maxRetries = 3;
      for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
        try {
          bookingResult = await handleBookingConfirmation(reservation, payload, studioCreativesData);
          if (bookingResult?.success && bookingResult?.booking) break;
        } catch (retryError) {
          if (isBookingAuthError(retryError)) {
            logBookingDiagnostic(DIAG, 'booking confirmation requires auth after authorized payment', { error: retryError });
            handleAuthRequired(payload);
            throw createUserFacingError(BOOKING_AUTH_RECOVERY_MESSAGE);
          }
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          } else {
            logBookingDiagnostic(DIAG, 'booking confirmation retry failed', { error: retryError });
            throw createUserFacingError(BOOKING_CREATION_AFTER_PAYMENT_ERROR_MESSAGE);
          }
        }
      }
      if (!bookingResult?.success || !bookingResult?.booking) {
        throw createUserFacingError(BOOKING_CREATION_AFTER_PAYMENT_ERROR_MESSAGE);
      }
      updateBookingData('paymentDraft', null);
      updateBookingData('id', bookingResult.booking.id);
      trackBookingPurchased({
        booking: bookingResult.booking,
        payload,
        reservation,
        source: "booking_desktop",
      });
      showConfirmation();
    },
    [handleBookingConfirmation, studioCreativesData, updateBookingData, showConfirmation, handleAuthRequired]
  );

  const paymentFlow = usePreparedBookingPaymentFlow({
    autoPrepare: false,
    canPreparePayment,
    validateProjectFields,
    buildBookingPayload,
    buildPayloadSignature,
    runSlotReservation,
    finalizeBookingAfterAuthorizedPayment,
    setBookingError,
    setPaymentProcessing,
    paymentProcessing,
    loading,
    canUseConnect,
    stripeConnectAccountId,
    onReservationStripeRoute: handleReservationStripeRoute,
    ensureReservationAmountReady,
    reservationCacheRef,
    onAuthRequired: handleAuthRequired,
    diagnosticLabel: DIAG,
    source: 'booking_desktop',
    messages: PAYMENT_FLOW_MESSAGES,
  });

  useEffect(() => {
    if (!bookingData?.startTime || !bookingData?.endTime || !studioCreativesData) return;
    const durationInHours = getBookingDurationInHours(bookingData.startTime, bookingData.endTime);
    if (durationInHours <= 0) return;
    // Clamp to minNumberOfHours (HOURLY mode) or use packageDurationHours (PACKAGES mode)
    const billableHours = studio?.pricingMode === 'PACKAGES'
      ? (Number(bookingData?.packageDurationHours) || durationInHours)
      : Math.max(durationInHours, Number(studio?.minNumberOfHours) || 1);
    const selectedPackages = getSelectedPackages(studioCreativesData, bookingData?.creativePackages || []);
    const totalCreativePrice = calculateCreativePackagesPrice(selectedPackages, billableHours).total;
    if (bookingData.totalCreativePrice !== totalCreativePrice) updateBookingData('totalCreativePrice', totalCreativePrice);
  }, [bookingData?.startTime, bookingData?.endTime, bookingData?.creativePackages, bookingData?.packageDurationHours, bookingData.totalCreativePrice, studio?.pricingMode, studio?.minNumberOfHours, studioCreativesData, updateBookingData]);

  const preparedReservation = paymentFlow.preparedPayment?.reservation || null;

  const elementsOptions = useMemo(
    () => ({
      clientSecret: preparedReservation?.clientSecret,
      loader: 'auto',
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#1F1F1F',
          colorBackground: '#ffffff',
          colorText: '#30313d',
          colorDanger: '#df1b41',
          borderRadius: '8px',
        },
      },
    }),
    [preparedReservation?.clientSecret]
  );

  const elementsInstanceKey = useMemo(
    () => `prepared-${studio?.id || 'na'}-${preparedReservation?.paymentIntentID || preparedReservation?.slotID || 'none'}`,
    [studio?.id, preparedReservation?.paymentIntentID, preparedReservation?.slotID]
  );

  return (
    <div className={classes.checkout} ref={stepScrollRootRef}>
      <div className={classes.checkoutContainer}>
        <div onClick={onPrevSlide} className={classes.footerButtonsContainer}>
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none"><line x1="9.60104" y1="0.726041" x2="0.602492" y2="9.72459" stroke="#1F1F1F" strokeWidth="1.7" /><line x1="0.626536" y1="8.54651" x2="9.62508" y2="18.2949" stroke="#1F1F1F" strokeWidth="1.7" /></svg>
          <button className={`${classes.footerBackButton} mediumLarge`}>Zurück</button>
        </div>
        <div className={classes.mainContainer}>
          <h1 className={`${classes.titleMain} titleSemibold2`}>Was planst du?</h1>
          <p className={`${classes.subtitle} slimRegularSmallText`}>Beschreibe dein Projekt, sodass der Host dir bestmöglich helfen kann.</p>
          <div className={classes.formContainer}>
            <div className={classes.inputWrapper} ref={projectNameRef}>
              <input onChange={(e) => { updateBookingData('projectName', e.target.value); clearFieldError('projectName'); }} type="text" placeholder="Projektname" className={`${classes.input} ${fieldErrors?.projectName ? classes.errorInput : ''} slimRegularLarge`} value={bookingData.projectName || ''} />
              {fieldErrors?.projectName && <div className={classes.errorMessage}><span className="slimRegularSmallText">{fieldErrors.projectName}</span></div>}
            </div>
            <input onChange={(e) => updateBookingData('companyName', e.target.value)} type="text" placeholder="Firmenname (optional)" className={`${classes.input} slimRegularLarge`} value={bookingData.companyName || ''} />
            <div className={classes.inputWrapper} ref={projectDescriptionRef}>
              <textarea onChange={(e) => { updateBookingData('description', e.target.value); clearFieldError('projectDescription'); }} placeholder="Projektbeschreibung" className={`${classes.textarea} ${fieldErrors.projectDescription ? classes.errorInput : ''} slimRegularLarge`} value={bookingData.description || ''} />
              {fieldErrors.projectDescription && <div className={classes.errorMessage}><span className="slimRegularSmallText">{fieldErrors.projectDescription}</span></div>}
            </div>
          </div>

          <div className={classes.horizontalLine} />
          <h2 className={`${classes.paymentTitle} titleSemibold2`}>Bezahlung</h2>
          <div className={classes.paymentOptions} ref={paymentMethodRef}>
            {!canPreparePayment ? (
              <p className="slimRegularSmallText">
                {bookingData?.pricingUnavailable
                  ? pricingUnavailableMessage
                  : 'Bitte Datum, Zeit und eine gültige Buchung wählen, damit der Betrag berechnet werden kann.'}
              </p>
            ) : paymentFlow.status === 'preparing' ? (
              <div className={classes.paymentPreparationSkeleton} aria-live="polite">
                <div className={`${classes.stripeLoadingMessage} slimRegularSmallText`}>
                  Buchung wird geprüft...
                </div>
                <div className={classes.paymentSkeletonLine} />
                <div className={classes.paymentSkeletonLine} />
              </div>
            ) : preparedReservation?.clientSecret ? (
              <Elements key={elementsInstanceKey} stripe={stripePromise} options={elementsOptions}>
                <PreparedStripePaymentSection
                  classes={classes}
                  buttonClassName={`${classes.acceptButton} regularLarge`}
                  paymentFlow={paymentFlow}
                  paymentProcessing={paymentProcessing}
                  loading={loading}
                />
              </Elements>
            ) : (
              <button
                type="button"
                onClick={() => paymentFlow.prepareBookingPayment()}
                className={`${classes.acceptButton} regularLarge`}
                disabled={paymentProcessing || loading}
              >
                {paymentFlow.status === 'expired' || paymentFlow.status === 'error'
                  ? 'Buchung erneut prüfen'
                  : 'Weiter zur Zahlung'}
              </button>
            )}
            {fieldErrors?.paymentMethod && <div className={classes.errorMessage}><span className="slimRegularSmallText">{fieldErrors.paymentMethod}</span></div>}
          </div>

          <div className={classes.horizontalLine} />
          <div className={classes.termsSection}>
            <p className={`${classes.agreementText} slimRegularSmallText`}>
              Mit der Bestätigung der Zahlung akzeptiere ich Stus Nutzungsbedingungen, Gemeinschaftsrichtlinien und Stornierungsbedingungen.
            </p>
            {bookingError && <div className={classes.errorMessage}>{bookingError}</div>}
            {error && <div className={classes.errorMessage}>Die Buchung konnte nicht erstellt werden. Bitte versuche es erneut.</div>}
          </div>
        </div>
      </div>
      <TotalBox studio={studio} style={{ width: 'fit-content' }} studioCreativesData={studioCreativesData} useStoreTotalPrice />
    </div>
  );
};

export default Checkout;
